import SimplexNoise from './SimplexNoise';

const PI = Math.PI;
const DEG_TO_RAD = PI / 180;
const DEFAULT_FRAME_MS = 1000 / 30;
const MAX_FRAME_GAP_MS = 1000;

const ACCELERATION_RANGE = { min: 0, max: 100 };
const SCALE_RANGE = { min: 0, max: 2 };
const SENSITIVITY_RANGE = { min: 0.01, max: 10 };

const SPECTRUM_MAGNITUDE_DIVISOR = 20;

// Spectrum magnitude arrives on the raw [0, 255] scale and is normalized by dividing
// by this divisor before use. Typical bass values land in ~[0.03, 0.2] after normalization.
// At max acceleration (100) and normalized bass ~0.1: speedMultiplier = 1 + 1 × 0.1 × 20 = 3×.
const ACCEL_SPEED_RANGE = 20;

// Noise lane seeds — must be distinct to produce independent patterns
const NOISE_LANE = {
    ROTATION: 7,
    X: 11,
    Y: 13
};

/**
 * @typedef {Object} DriftConfig
 * @property {boolean}  enabled       - Whether the drift effect is active
 * @property {number}   amplitudeX    - Pixel displacement along X axis
 * @property {number}   amplitudeY    - Pixel displacement along Y axis
 * @property {number}   rotation      - Rotation amplitude in degrees
 * @property {number}   speed         - Noise time scale multiplier
 * @property {number}   octaves       - fBm octave count (≥1)
 * @property {number}   scale         - Audio-reactive scale factor [0, 1]
 * @property {number}   acceleration  - Audio offset/scale boost [0, 100]
 * @property {boolean}  coverEdges    - Auto-scale to hide exposed borders
 * @property {number}   [sensitivity] - Audio level multiplier (default 1.0, range [0.01, 10])
 * @property {number}   [maxZoom]     - Maximum audio-reactive scale (default 1 = no zoom; e.g. 1.15 = up to 15% zoom). Decoupled from edge cover.
 */

export default class DriftEffect {

    constructor() {
        // Deterministic seed ensures identical drift across preview and export
        this._noise = new SimplexNoise(42);
        this._effectiveTime = 0;
        this._lastFrameTime = 0;
        this._lastFrameIndex = -1;

        this._resetParameters();
        this._resetBaseTransform();
        this._resetDimensions();
    }

    // ── Configuration ────────────────────────────────────────────────────

    /**
     * Updates the drift configuration. Pass `null` or a config with
     * `enabled: false` to disable the effect.
     * @param {DriftConfig|null} config
     */
    updateConfig(config) {
        if (!config) {
            this.enabled = false;
            return;
        }

        this.enabled = Boolean(config.enabled);
        this.amplitudeX = config.amplitudeX ?? 0;
        this.amplitudeY = config.amplitudeY ?? 0;
        this.rotation = config.rotation ?? 0;
        this.speed = config.speed ?? 1;
        this.octaves = Math.max(1, config.octaves ?? 1);
        this.scale = clamp(config.scale ?? 0, SCALE_RANGE.min, SCALE_RANGE.max);
        this.acceleration = clamp(config.acceleration ?? 0, ACCELERATION_RANGE.min, ACCELERATION_RANGE.max);
        this.coverEdges = Boolean(config.coverEdges);
        this.sensitivity = clamp(config.sensitivity ?? 1, SENSITIVITY_RANGE.min, SENSITIVITY_RANGE.max);
        this.maxZoom = Math.max(1, config.maxZoom ?? 1);

        // Reset delta-time tracking so the first frame after config change is clean
        this._lastFrameTime = 0;

        this._updateEdgeCoverScale();
    }

    /**
     * Sets the base (rest) transform that the drift offsets are applied on top of.
     * @param {{ x: number, y: number }} position
     * @param {number} rotation - Base rotation in radians
     * @param {{ x: number, y: number }} scale
     */
    setBaseTransform(position, rotation, scale) {
        this.basePosition = { x: position.x, y: position.y };
        this.baseRotation = rotation;
        this.baseScale = { x: scale.x, y: scale.y };
    }

    /**
     * Sets the element dimensions used for edge-cover scale computation.
     * @param {number} width
     * @param {number} height
     */
    setDimensions(width, height) {
        this.width = width;
        this.height = height;
        this._updateEdgeCoverScale();
    }

    // ── Transform Computation ────────────────────────────────────────────

    /**
     * Returns the procedural drift transform for the current frame,
     * or null when the effect is inactive.
     *
     * `spectrumMagnitude` is expected to be a single number on the raw spectrum scale
     * (typically an average in the range ~[0,255]). DriftEffect normalizes it internally
     * and multiplies it by the `sensitivity` parameter before use.
     *
     * @param {number} [spectrumMagnitude=0]
     * @param {number} [frameIndex=-1]
     * @param {number} [fps=60]
     * @returns {{ translateX: number, translateY: number, rotation: number, scale: number } | null}
     */
    compute(spectrumMagnitude = 0, frameIndex = -1, fps = 60) {
        const hasDisplacement = this.amplitudeX || this.amplitudeY || this.rotation;
        const hasAudioScale = this.scale > 0;

        if (!this.enabled || (!hasDisplacement && !hasAudioScale))
            return null;

        // Normalize raw magnitude and apply sensitivity; result is clamped to [0, 1]
        const audioLevel = this._resolveAudioLevel(spectrumMagnitude / SPECTRUM_MAGNITUDE_DIVISOR);

        let dtSec = 0;

        if (frameIndex >= 0 && fps > 0) {
            // Deterministic mode (frame-by-frame rendering or deterministic real-time preview)
            dtSec = 1 / fps;
            
            // Seeking backwards or uninitialized
            if (this._lastFrameIndex === -1 || frameIndex < this._lastFrameIndex) {
                this._effectiveTime = (frameIndex / fps);
            }
            this._lastFrameIndex = frameIndex;
        } else {
            // Real-time preview mode (performance.now() fallback)
            const now = performance.now();
            let dt = now - this._lastFrameTime;
            if (!dt || dt > MAX_FRAME_GAP_MS) dt = DEFAULT_FRAME_MS;
            this._lastFrameTime = now;
            dtSec = dt / 1000;
        }

        // Time-domain acceleration: bass speeds up noise traversal, not position directly.
        // When audio drops, speed returns to baseline — no snap-back.
        const speedMultiplier = 1 + (this.acceleration / 100) * audioLevel * ACCEL_SPEED_RANGE;

        this._effectiveTime += this.speed * Math.max(0, speedMultiplier) * dtSec;

        const t = this._effectiveTime;

        let finalOffsetX = 0;
        let finalOffsetY = 0;
        let angle = 0;

        if (hasDisplacement) {
            const { offsetX, offsetY, rotation } = this._sampleNoise(t);
            finalOffsetX = offsetX;
            finalOffsetY = offsetY;
            angle = rotation * DEG_TO_RAD;
        }

        // Edge-cover scale: enlarge just enough to hide exposed edges based on max configured amplitudes.
        // We include the current dynamic scale as a fallback because during config transitions (e.g., user
        // lowering amplitude via slider), the current noise offset may temporarily exceed
        // the new amplitude bounds.
        const edgeCoverScale = this.coverEdges
            ? Math.max(1, this._maxScale, this.computeEdgeCoverScale(finalOffsetX, finalOffsetY, angle))
            : 1;

        // Audio-reactive scale — ceiling may exceed _maxScale when maxZoom is configured
        const bassScale = hasAudioScale
            ? this._computeAudioScale(audioLevel)
            : 1;

        const finalScale = Math.max(edgeCoverScale, bassScale);

        return {
            translateX: finalOffsetX,
            translateY: finalOffsetY,
            rotation: angle,
            scale: finalScale
        };
    }

    /**
     * Convenience method: computes the drift transform and applies it
     * directly to a PIXI DisplayObject.
     * @param {PIXI.DisplayObject} target
     * @param {number} [spectrumMagnitude=0]
     */
    applyTo(target, spectrumMagnitude = 0) {
        const transform = this.compute(spectrumMagnitude);

        if (!transform) {
            target.position.set(this.basePosition.x, this.basePosition.y);
            target.rotation = this.baseRotation;
            target.scale.set(this.baseScale.x, this.baseScale.y);
            return;
        }

        target.position.set(
            this.basePosition.x + transform.translateX,
            this.basePosition.y + transform.translateY
        );
        target.rotation = this.baseRotation + transform.rotation;
        target.scale.set(
            this.baseScale.x * transform.scale,
            this.baseScale.y * transform.scale
        );
    }

    // ── Edge-Cover Scale ─────────────────────────────────────────────────

    /**
     * Computes the minimum uniform scale that guarantees full coverage
     * of the original bounds under a given translation + rotation offset.
     * @param {number} tx - X offset in pixels
     * @param {number} ty - Y offset in pixels
     * @param {number} angle - Rotation offset in radians
     * @returns {number}
     */
    computeEdgeCoverScale(tx, ty, angle) {
        if (!this.width || !this.height) return 1;

        let scale = 1;

        // Normalize angle into [0, PI) — the bounding box is symmetric per half-turn
        const normalizedAngle = angle - PI * Math.floor(angle / PI);

        if (normalizedAngle !== 0) {
            const sin = Math.sin(normalizedAngle);
            const cos = Math.cos(normalizedAngle);
            const hw = this.width / 2;
            const hh = this.height / 2;

            scale = 2 * Math.max(
                Math.abs(hw * cos - hh * sin) / this.width,
                Math.abs(hw * sin + hh * cos) / this.height,
                Math.abs(-hw * cos - hh * sin) / this.width,
                Math.abs(-hw * sin + hh * cos) / this.height
            );
        }

        // Expand to compensate for translation
        scale *= Math.max(
            (2 * Math.abs(tx) + this.width) / this.width,
            (2 * Math.abs(ty) + this.height) / this.height
        );

        return scale;
    }

    // ── Lifecycle ────────────────────────────────────────────────────────

    /**
     * Resets the drift timeline to zero without tearing down the instance.
     * Useful when seeking or restarting playback.
     */
    reset() {
        this._effectiveTime = 0;
        this._lastFrameTime = 0;
        this._lastFrameIndex = -1;
    }

    /**
     * Releases internal resources. Call when the owner element is destroyed.
     */
    destroy() {
        this._noise = null;
        this.enabled = false;
    }

    // ── Private Helpers ──────────────────────────────────────────────────

    /**
     * Applies sensitivity to a normalized audio level and clamps the result to [0, 1].
     * @param {number} audioLevel - Pre-normalized value (spectrum / SPECTRUM_MAGNITUDE_DIVISOR)
     * @returns {number}
     */
    _resolveAudioLevel(audioLevel) {
        return clamp(audioLevel * this.sensitivity, 0, 1);
    }

    _resetParameters() {
        this.enabled = false;
        this.amplitudeX = 0;
        this.amplitudeY = 0;
        this.rotation = 0;
        this.speed = 1;
        this.octaves = 1;
        this.scale = 0;
        this.acceleration = 0;
        this.coverEdges = false;
        this.sensitivity = 1;
        this.maxZoom = 1;
    }

    _resetBaseTransform() {
        this.basePosition = { x: 0, y: 0 };
        this.baseRotation = 0;
        this.baseScale = { x: 1, y: 1 };
    }

    _resetDimensions() {
        this.width = 0;
        this.height = 0;
        this._maxScale = 1;
        this._audioScaleCeil = 1;
    }

    _updateEdgeCoverScale() {
        this._maxScale = this.computeEdgeCoverScale(
            this.amplitudeX,
            this.amplitudeY,
            this.rotation * DEG_TO_RAD
        );
        
        // Audio scale ceiling: whichever is larger — edge cover or explicit maxZoom
        this._audioScaleCeil = Math.max(this._maxScale, this.maxZoom);
    }

    /**
     * Samples multi-octave simplex noise and returns raw offset/rotation
     * values scaled by their respective amplitudes.
     */
    _sampleNoise(t) {
        let offsetX = 0;
        let offsetY = 0;
        let rotation = 0;
        let weightSum = 0;

        for (let i = 0; i < this.octaves; i++) {
            const freq = 2 ** i;
            const weight = 0.5 ** i;
            weightSum += weight;

            if (this.rotation)
                rotation += this._sample(t, freq, NOISE_LANE.ROTATION) * weight;
            if (this.amplitudeX)
                offsetX += this._sample(t, freq, NOISE_LANE.X) * weight;
            if (this.amplitudeY)
                offsetY += this._sample(t, freq, NOISE_LANE.Y) * weight;
        }

        return {
            offsetX: offsetX * this.amplitudeX / weightSum,
            offsetY: offsetY * this.amplitudeY / weightSum,
            rotation: rotation * this.rotation / weightSum
        };
    }

    /**
     * Computes audio-reactive scale from magnitude and the configured scale parameter.
     */
    _computeAudioScale(magnitude) {
        const ceil = this._audioScaleCeil;
        return clamp(1 + (ceil - 1) * magnitude * this.scale, 1, ceil);
    }


    _sample(t, freq, lane) {
        return this._noise.noise2D(t * freq, lane * freq);
    }
}

// ── Utility ──────────────────────────────────────────────────────────────

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}